import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Save, Plus, Pencil, Trash2, Signature, Sparkles, Settings, Send, Wrench, BarChart3 } from 'lucide-react';
import RichTextEditor from '../../components/bandeja/RichTextEditor';
import MigracionYahooPanel from '../../components/bandeja/MigracionYahooPanel';
import HidratacionProactivaPanel from '../../components/bandeja/HidratacionProactivaPanel';
import { GoogleAddForm, ImapAddForm } from './BandejaUnificadaPage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BandejaConfiguracionPage() {
  const [accounts, setAccounts] = useState([]);
  const [canned, setCanned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addTab, setAddTab] = useState('google');

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API_URL}/bandeja-unificada/accounts`),
        axios.get(`${API_URL}/bandeja-unificada/canned-responses`),
      ]);
      setAccounts(a.data || []);
      setCanned(c.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" /></div>;

  return (
    <div className="space-y-6" data-testid="bandeja-config-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-600" /> Configuración de la Bandeja
          </h1>
          <p className="text-sm text-gray-600">Cuentas, firmas, respuestas frecuentes y estadísticas.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link
            to="/admin/bandeja-unificada/estadisticas"
            className="inline-flex items-center gap-1 px-3 py-1.5 border rounded hover:bg-gray-50 text-sm"
            data-testid="btn-nav-stats"
          >
            <BarChart3 className="w-4 h-4" /> Estadísticas
          </Link>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => setAddOpen(true)}
            data-testid="btn-add-account"
          >
            <Plus className="w-4 h-4 mr-1" /> Agregar cuenta
          </Button>
        </div>
      </div>

      {/* Modal Agregar cuenta */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-add-account">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Agregar cuenta</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 border-b mb-3">
            <button
              onClick={() => setAddTab('google')}
              className={`px-3 py-1.5 text-sm font-medium border-b-2 ${addTab === 'google' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500'}`}
              data-testid="tab-add-google"
            >Google (Gmail)</button>
            <button
              onClick={() => setAddTab('imap')}
              className={`px-3 py-1.5 text-sm font-medium border-b-2 ${addTab === 'imap' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500'}`}
              data-testid="tab-add-imap"
            >IMAP (Yahoo/otros)</button>
          </div>
          {addTab === 'google'
            ? <GoogleAddForm onDone={() => { setAddOpen(false); load(); }} />
            : <ImapAddForm onDone={() => { setAddOpen(false); load(); }} />}
        </DialogContent>
      </Dialog>

      <MigracionYahooPanel accounts={accounts} />

      <HidratacionProactivaPanel />

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Signature className="w-4 h-4" /> Firmas por cuenta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {accounts.length === 0 && <p className="text-sm text-gray-500 italic">Sin cuentas conectadas.</p>}
          {accounts.map(a => (
            <FirmaEditor key={a.id} account={a} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> Respuestas frecuentes</CardTitle>
          <NewCannedButton onCreated={load} />
        </CardHeader>
        <CardContent>
          <CannedList items={canned} onChange={load} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4" /> Follow-up automático de registro</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Se envía desde <b>info@colegiotslomas.org</b> 2 horas después de marcar como &quot;Resuelto&quot;
            una conversación de un matriculado sin usuario creado. Si el estado cambia antes, se cancela automáticamente.
            Variables disponibles: <code className="text-purple-700">{`{{nombre_matriculado}}`}</code> y <code className="text-purple-700">{`{{link_registration}}`}</code>.
          </p>
        </CardHeader>
        <CardContent>
          <FollowupRegistroEditor />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Wrench className="w-4 h-4" /> Reparar adjuntos históricos</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Re-descarga desde el servidor original los adjuntos e imágenes inline (cid:)
            de correos que quedaron sin persistir por un bug previo. Idempotente: podés
            correrlo varias veces sin romper nada.
          </p>
        </CardHeader>
        <CardContent>
          <RepararAdjuntosHistoricos />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Wrench className="w-4 h-4" /> Separar conversaciones por remitente</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Migración one-shot para conversaciones históricas que quedaron mezcladas
            porque varios matriculados respondieron al mismo mail masivo. Divide una
            conv en varias, una por remitente. A partir de ahora el sync ya no las
            mezcla. Idempotente.
          </p>
        </CardHeader>
        <CardContent>
          <SepararConversaciones />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Trash2 className="w-4 h-4" /> Limpiar mensajes duplicados</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Detecta y elimina mensajes duplicados en la base — primero por
            <code className="bg-gray-100 px-1 rounded mx-1">Message-ID</code> exacto,
            después por la combinación <em>Subject + From + Fecha</em> para atrapar
            los que perdieron el ID. Se conserva la versión con cuerpo/adjuntos
            (o la más antigua por <code className="bg-gray-100 px-1 rounded">synced_at</code>
            si empatan). Corrélo primero con <strong>Vista previa</strong> para ver
            cuántos borraría sin tocar nada.
          </p>
        </CardHeader>
        <CardContent>
          <LimpiarDuplicados />
        </CardContent>
      </Card>
    </div>
  );
}


function FollowupRegistroEditor() {
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/configuracion-mensajes/mail_followup_registro`);
        setAsunto(r.data?.asunto || '');
        setCuerpo(r.data?.cuerpo_html || '');
      } catch (e) { setErr(e.response?.data?.detail || 'Error cargando template'); }
      setLoaded(true);
    })();
  }, []);

  const guardar = async () => {
    setSaving(true); setSaved(false); setErr(null);
    try {
      await axios.put(
        `${API_URL}/bandeja-unificada/configuracion-mensajes/mail_followup_registro`,
        { asunto, cuerpo_html: cuerpo },
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Loader2 className="w-5 h-5 animate-spin text-purple-600" />;
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Asunto</Label>
        <Input value={asunto} onChange={e => setAsunto(e.target.value)} data-testid="followup-asunto" />
      </div>
      <div>
        <Label className="text-xs">Cuerpo</Label>
        <RichTextEditor value={cuerpo} onChange={setCuerpo} />
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={guardar} disabled={saving} data-testid="followup-guardar">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
        {saved && <span className="text-xs text-green-700">Guardado ✓</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    </div>
  );
}

function FirmaEditor({ account }) {
  const [firma, setFirma] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingReplyTo, setSavingReplyTo] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedName, setSavedName] = useState(false);
  const [savedReplyTo, setSavedReplyTo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rf, rn, rr] = await Promise.all([
          axios.get(`${API_URL}/bandeja-unificada/accounts/${account.id}/firma`),
          axios.get(`${API_URL}/bandeja-unificada/accounts/${account.id}/from-name`),
          axios.get(`${API_URL}/bandeja-unificada/accounts/${account.id}/reply-to`),
        ]);
        setFirma(rf.data.firma_html || '');
        setFromName(rn.data.from_name || '');
        setReplyTo(rr.data.reply_to || '');
      } catch (e) { /* silent */ }
      setLoaded(true);
    })();
  }, [account.id]);

  const guardar = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.put(`${API_URL}/bandeja-unificada/accounts/${account.id}/firma`, { firma_html: firma });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const guardarNombre = async () => {
    setSavingName(true); setSavedName(false);
    try {
      await axios.put(`${API_URL}/bandeja-unificada/accounts/${account.id}/from-name`, { from_name: fromName });
      setSavedName(true);
      setTimeout(() => setSavedName(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSavingName(false);
  };

  const guardarReplyTo = async () => {
    setSavingReplyTo(true); setSavedReplyTo(false);
    try {
      await axios.put(`${API_URL}/bandeja-unificada/accounts/${account.id}/reply-to`, { reply_to: replyTo });
      setSavedReplyTo(true);
      setTimeout(() => setSavedReplyTo(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSavingReplyTo(false);
  };

  return (
    <div className="border-l-4 pl-4 space-y-3" style={{ borderColor: account.color || '#7c3aed' }} data-testid={`firma-editor-${account.id}`}>
      <div>
        <p className="font-medium text-gray-900">{account.label}</p>
        <p className="text-xs text-gray-500">{account.email}</p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Nombre visible del remitente</Label>
        <div className="flex items-center gap-2">
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Ej: CATSLZ - Información"
            className="max-w-md"
            data-testid={`from-name-${account.id}`}
          />
          <Button size="sm" onClick={guardarNombre} disabled={savingName} variant="outline" data-testid={`btn-guardar-nombre-${account.id}`}>
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1">Guardar</span>
          </Button>
          {savedName && <span className="text-xs text-emerald-600">✓</span>}
        </div>
        <p className="text-[11px] text-gray-500">
          Se mostrará como <code className="bg-gray-100 px-1 rounded">{fromName ? `"${fromName}" <${account.email}>` : account.email}</code> en la casilla de quien reciba el correo.
        </p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Responder a (Reply-To)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder={`Ej: consultas@catslz.org (por defecto: ${account.email})`}
            className="max-w-md"
            data-testid={`reply-to-${account.id}`}
          />
          <Button size="sm" onClick={guardarReplyTo} disabled={savingReplyTo} variant="outline" data-testid={`btn-guardar-reply-to-${account.id}`}>
            {savingReplyTo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1">Guardar</span>
          </Button>
          {savedReplyTo && <span className="text-xs text-emerald-600">✓</span>}
        </div>
        <p className="text-[11px] text-gray-500">
          Cuando el destinatario le da &quot;Responder&quot;, la respuesta va a esta dirección. Si está vacío, va al mismo correo de envío.
        </p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Firma</Label>
        {loaded && <RichTextEditor value={firma} onChange={setFirma} placeholder="Ej: — Secretaría, CATSLZ" />}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={guardar} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid={`btn-guardar-firma-${account.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1">Guardar firma</span>
          </Button>
          {saved && <span className="text-xs text-emerald-600">✓ Guardada</span>}
        </div>
      </div>
    </div>
  );
}

function NewCannedButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-new-canned">
        <Plus className="w-4 h-4 mr-1" /> Nueva
      </Button>
      {open && <CannedFormDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onCreated?.(); }} />}
    </>
  );
}

function CannedFormDialog({ item, onClose, onSaved }) {
  const [titulo, setTitulo] = useState(item?.titulo || '');
  const [categoria, setCategoria] = useState(item?.categoria || '');
  const [contenido, setContenido] = useState(item?.contenido_html || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    if (!titulo.trim()) { setError('Título obligatorio'); return; }
    setSaving(true); setError('');
    try {
      if (item) {
        await axios.put(`${API_URL}/bandeja-unificada/canned-responses/${item.id}`, { titulo, categoria, contenido_html: contenido });
      } else {
        await axios.post(`${API_URL}/bandeja-unificada/canned-responses`, { titulo, categoria, contenido_html: contenido });
      }
      onSaved?.();
    } catch (e) { setError(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? 'Editar respuesta frecuente' : 'Nueva respuesta frecuente'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Título *</Label>
            <Input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej: Requisitos para certificado" data-testid="canned-titulo" />
          </div>
          <div>
            <Label>Categoría</Label>
            <Input value={categoria} onChange={e => setCategoria(e.target.value)} placeholder="Ej: Secretaría" data-testid="canned-categoria" />
          </div>
          <div>
            <Label>Contenido</Label>
            <RichTextEditor value={contenido} onChange={setContenido} placeholder="Escribí el texto que se insertará al elegir esta respuesta…" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="canned-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CannedList({ items, onChange }) {
  const [editing, setEditing] = useState(null);

  const borrar = async (item) => {
    if (!window.confirm(`¿Borrar la respuesta frecuente "${item.titulo}"?`)) return;
    try {
      await axios.delete(`${API_URL}/bandeja-unificada/canned-responses/${item.id}`);
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (items.length === 0) return <p className="text-sm text-gray-500 italic">Aún no hay respuestas frecuentes.</p>;
  return (
    <>
      <div className="space-y-2" data-testid="canned-list">
        {items.map(c => (
          <div key={c.id} className="border rounded p-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{c.titulo}</p>
              {c.categoria && <p className="text-xs text-gray-500 mb-1">{c.categoria}</p>}
              <div className="text-sm text-gray-600 prose prose-sm max-w-none line-clamp-3" dangerouslySetInnerHTML={{ __html: c.contenido_html }} />
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => setEditing(c)} className="p-1.5 text-gray-500 hover:text-purple-600" title="Editar" data-testid={`canned-edit-${c.id}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => borrar(c)} className="p-1.5 text-gray-500 hover:text-red-600" title="Borrar" data-testid={`canned-delete-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && <CannedFormDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange?.(); }} />}
    </>
  );
}

function RepararAdjuntosHistoricos() {
  const [scanning, setScanning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [scan, setScan] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(15);
  const [jobId, setJobId] = useState(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const escanear = async () => {
    setScanning(true); setError(''); setResult(null); setScan(null);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/admin/reparar-adjuntos-historicos?limit=${limit}&dry_run=true`,
      );
      setScan(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al escanear');
    } finally {
      setScanning(false);
    }
  };

  const pollJob = async (id) => {
    try {
      const r = await axios.get(
        `${API_URL}/bandeja-unificada/admin/reparar-adjuntos-historicos/jobs/${id}`,
      );
      setResult(r.data);
      if (r.data?.status === 'completed' || r.data?.status === 'failed') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setRepairing(false);
        setJobId(null);
      }
    } catch (e) {
      // Silenciar errores transitorios del polling; si es 404, cortamos.
      if (e.response?.status === 404) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setRepairing(false);
        setJobId(null);
        setError('Job perdido en el servidor');
      }
    }
  };

  const reparar = async () => {
    if (!window.confirm(
      `¿Reparar hasta ${limit} correos por cuenta? La reparación corre en segundo ` +
      `plano; vas a ver el progreso acá abajo.`,
    )) return;
    setRepairing(true); setError(''); setResult(null); setScan(null);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/admin/reparar-adjuntos-historicos?limit=${limit}&dry_run=false`,
      );
      const id = r.data?.job_id;
      if (!id) throw new Error('Respuesta sin job_id');
      setJobId(id);
      // Primera consulta inmediata + poll cada 3s
      await pollJob(id);
      pollRef.current = setInterval(() => pollJob(id), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar la reparación');
      setRepairing(false);
    }
  };

  const enProgreso = repairing && result?.status === 'running';

  return (
    <div className="space-y-3" data-testid="reparar-adjuntos-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Máximo por cuenta (1-50)</Label>
          <Input
            type="number"
            min={1}
            max={50}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(50, parseInt(e.target.value || '15', 10))))}
            className="w-24"
            data-testid="reparar-adjuntos-limit"
          />
        </div>
        <Button
          variant="outline"
          onClick={escanear}
          disabled={scanning || repairing}
          data-testid="reparar-adjuntos-escanear"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Escanear candidatos
        </Button>
        <Button
          onClick={reparar}
          disabled={scanning || repairing}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="reparar-adjuntos-ejecutar"
        >
          {repairing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          {enProgreso ? 'Reparando…' : 'Reparar ahora'}
        </Button>
        {jobId && (
          <span className="text-xs text-gray-500 font-mono" data-testid="reparar-adjuntos-jobid">
            Job: {jobId.slice(0, 8)}…
          </span>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600" data-testid="reparar-adjuntos-error">{error}</p>
      )}

      {scan && (
        <div className="border rounded p-3 bg-purple-50 text-sm space-y-1" data-testid="reparar-adjuntos-scan">
          <p><b>{scan.candidatos}</b> correos candidatos en <b>{scan.cuentas}</b> cuenta(s).</p>
          {scan.por_cuenta && scan.por_cuenta.length > 0 && (
            <ul className="list-disc pl-5">
              {scan.por_cuenta.map((c) => (
                <li key={c.account_id}>{c.account_label}: {c.candidatos}</li>
              ))}
            </ul>
          )}
          {scan.candidatos === 0 && (
            <p className="text-emerald-700">No hay nada que reparar 🎉</p>
          )}
        </div>
      )}

      {result && (
        <div
          className={`border rounded p-3 text-sm space-y-1 ${
            result.status === 'failed' ? 'bg-red-50'
            : result.status === 'running' ? 'bg-amber-50'
            : 'bg-emerald-50'
          }`}
          data-testid="reparar-adjuntos-result"
        >
          <p className="font-medium">
            {result.status === 'running' && <span className="text-amber-800">En progreso…</span>}
            {result.status === 'completed' && <span className="text-emerald-800">Reparación completada</span>}
            {result.status === 'failed' && <span className="text-red-800">Reparación falló</span>}
          </p>
          <p>Candidatos totales: <b>{result.candidatos || 0}</b> en {result.cuentas || 0} cuenta(s)</p>
          <p>Mensajes reparados: <b>{result.totales?.mensajes_reparados || 0}</b></p>
          <p>Adjuntos recuperados: <b>{result.totales?.adjuntos || 0}</b></p>
          <p>Imágenes inline recuperadas: <b>{result.totales?.inline || 0}</b></p>
          {(result.totales?.sin_datos || 0) > 0 && (
            <p className="text-gray-600">Sin datos re-descargables: {result.totales.sin_datos}</p>
          )}
          {(result.totales?.errores || 0) > 0 && (
            <p className="text-red-600">Errores: {result.totales.errores}</p>
          )}
          {result.error && <p className="text-red-700 text-xs">{result.error}</p>}
          {result.por_cuenta && result.por_cuenta.length > 0 && (
            <details className="mt-2" open={result.status === 'running'}>
              <summary className="cursor-pointer text-xs text-gray-600">Detalle por cuenta</summary>
              <ul className="list-disc pl-5 text-xs mt-1">
                {result.por_cuenta.map((c) => (
                  <li key={c.account_id}>
                    {c.account_label}: {c.mensajes_reparados || 0}/{c.candidatos} reparados
                    {(c.errores > 0) && ` — ${c.errores} error(es)`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}



function SepararConversaciones() {
  const [scanning, setScanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(100);

  const ejecutar = async (dryRun) => {
    if (!dryRun && !window.confirm(
      `¿Separar las conversaciones mezcladas? Esta operación divide una conv en varias ` +
      `cuando detecta múltiples remitentes distintos. Es idempotente pero irreversible.`,
    )) return;
    if (dryRun) setScanning(true); else setRunning(true);
    setError(''); setResult(null);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/admin/separar-conversaciones-por-remitente?limit=${limit}&dry_run=${dryRun}`,
      );
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al procesar');
    } finally {
      setScanning(false); setRunning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="separar-convs-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Máximo de convs</Label>
          <Input
            type="number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(500, parseInt(e.target.value || '100', 10))))}
            className="w-24"
            data-testid="separar-convs-limit"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => ejecutar(true)}
          disabled={scanning || running}
          data-testid="separar-convs-escanear"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Escanear convs sucias
        </Button>
        <Button
          onClick={() => ejecutar(false)}
          disabled={scanning || running}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="separar-convs-ejecutar"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          Separar ahora
        </Button>
      </div>

      {error && (
        <p className="text-sm text-red-600" data-testid="separar-convs-error">{error}</p>
      )}

      {result && (
        <div
          className={`border rounded p-3 text-sm space-y-1 ${
            result.dry_run ? 'bg-purple-50' : 'bg-emerald-50'
          }`}
          data-testid="separar-convs-result"
        >
          <p className="font-medium">
            {result.dry_run
              ? `${result.convs_analizadas} conversación(es) mezcladas encontradas`
              : `Migración completada`}
          </p>
          <p>Conversaciones analizadas: <b>{result.convs_analizadas || 0}</b></p>
          <p>Conversaciones nuevas creadas: <b>{result.convs_creadas || 0}</b></p>
          <p>Mensajes movidos: <b>{result.mensajes_movidos || 0}</b></p>
          {result.convs_analizadas === 0 && (
            <p className="text-emerald-700">No hay convs mezcladas 🎉</p>
          )}
          {result.por_conv && result.por_conv.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-gray-600">Detalle por conversación</summary>
              <ul className="list-disc pl-5 text-xs mt-1 space-y-0.5">
                {result.por_conv.slice(0, 50).map((c, i) => (
                  <li key={i}>
                    <span className="font-medium">{c.asunto || '(sin asunto)'}</span>
                    {c.ganador && <> — original: {c.ganador}</>}
                    {c.convs_creadas > 0 && <>, {c.convs_creadas} conv(s) nueva(s)</>}
                    {c.mensajes_movidos > 0 && <>, {c.mensajes_movidos} msg(s) movido(s)</>}
                    {c.error && <span className="text-red-600"> — {c.error}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function LimpiarDuplicados() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [incluirHeuristica, setIncluirHeuristica] = useState(true);

  const ejecutar = async (dryRun) => {
    setError('');
    setResult(null);
    if (!dryRun) {
      const total = result?.total_encontrados ?? null;
      const msg = total != null
        ? `Se eliminarán ~${total} mensajes duplicados. Esta acción no se puede deshacer. ¿Continuar?`
        : '¿Ejecutar la limpieza definitiva de mensajes duplicados? Esta acción no se puede deshacer.';
      if (!window.confirm(msg)) return;
    }
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/bandeja-unificada/admin/limpiar-duplicados`, {
        dry_run: dryRun,
        incluir_heuristica_subject: incluirHeuristica,
      }, { timeout: 300000 });
      // Enriquecer con total_encontrados para la confirmación siguiente
      const tf = (r.data?.por_message_id?.duplicados_encontrados || 0)
        + (r.data?.por_subject_from_fecha?.duplicados_encontrados || 0);
      setResult({ ...r.data, total_encontrados: tf });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo ejecutar la limpieza');
    }
    setRunning(false);
  };

  return (
    <div className="space-y-3" data-testid="limpiar-duplicados">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={incluirHeuristica}
          onChange={e => setIncluirHeuristica(e.target.checked)}
          data-testid="limpiar-dup-heuristica-toggle"
        />
        <span>Incluir heurística <em>Subject + From + Fecha</em> (recomendado)</span>
      </label>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => ejecutar(true)}
          disabled={running}
          data-testid="limpiar-dup-preview"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
          Vista previa (dry run)
        </Button>
        <Button
          onClick={() => ejecutar(false)}
          disabled={running || !result || result.total_encontrados === 0}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="limpiar-dup-ejecutar"
          title={!result ? 'Corré primero Vista previa' : result.total_encontrados === 0 ? 'No hay duplicados que borrar' : 'Elimina definitivamente los duplicados'}
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Eliminar duplicados
        </Button>
      </div>

      {error && <p className="text-sm text-red-600" data-testid="limpiar-dup-error">{error}</p>}

      {result && (
        <div
          className={`border rounded p-3 text-sm space-y-1 ${result.dry_run ? 'bg-purple-50' : 'bg-emerald-50'}`}
          data-testid="limpiar-dup-result"
        >
          <p className="font-medium">
            {result.dry_run
              ? (result.total_encontrados === 0
                  ? 'Sin duplicados encontrados 🎉'
                  : `Vista previa: se detectaron ${result.total_encontrados} duplicados`)
              : `Limpieza completada: ${result.total_eliminados} mensajes eliminados`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div className="border rounded p-2 bg-white/60">
              <p className="text-xs uppercase text-gray-500 tracking-wide">Por Message-ID</p>
              <p>Grupos: <b>{result.por_message_id?.grupos_duplicados || 0}</b></p>
              <p>Duplicados: <b>{result.por_message_id?.duplicados_encontrados || 0}</b></p>
              <p>Eliminados: <b>{result.por_message_id?.eliminados || 0}</b></p>
            </div>
            {result.por_subject_from_fecha && (
              <div className="border rounded p-2 bg-white/60">
                <p className="text-xs uppercase text-gray-500 tracking-wide">Por Subject + From + Fecha</p>
                <p>Grupos: <b>{result.por_subject_from_fecha.grupos_duplicados || 0}</b></p>
                <p>Duplicados: <b>{result.por_subject_from_fecha.duplicados_encontrados || 0}</b></p>
                <p>Eliminados: <b>{result.por_subject_from_fecha.eliminados || 0}</b></p>
              </div>
            )}
          </div>
          {result.dry_run && result.total_encontrados > 0 && (
            <p className="text-xs text-purple-700 mt-2">
              Si el resultado te parece correcto, tocá <strong>Eliminar duplicados</strong> para borrar
              definitivamente. Los duplicados se eliminan conservando la versión con cuerpo/adjuntos.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
